import CustomModal from "@/shared/components/ui/Modal/CustomModal";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import { X } from "lucide-react";
import React from "react";
import { useUpdateFollowUpReminder } from "@/features/accounts/hooks/useAccountRelationship";
import { useDismissFollowUpSuggestion } from "@/features/accounts/hooks/useAccounts";

const FollowUpActionModal = ({
  followUp,
  isOpen,
  onClose,
  onOpenReminder,
  onOpenEmail,
  onOpenAccount,
  onCompleted,
}) => {
  const hasContact = Boolean(followUp?.relation_contact_id);
  const isScheduledFollowUp = followUp?.item_type === "follow_up";
  const isAiSuggestion = followUp?.item_type === "ai_suggestion";
  const canComplete =
    (isScheduledFollowUp || isAiSuggestion) && Boolean(followUp?.follow_up_id);
  const { mutate: updateFollowUpReminder, isPending: isCompleting } =
    useUpdateFollowUpReminder({ action: onCompleted });
  const { mutate: dismissSuggestion, isPending: isDismissing } =
    useDismissFollowUpSuggestion({ action: onCompleted });

  const handleComplete = () => {
    if (isAiSuggestion) {
      dismissSuggestion({ suggestionId: followUp.follow_up_id });
      return;
    }
    updateFollowUpReminder({
      followUpId: followUp.follow_up_id,
      relationshipMappingId: followUp.relation_contact_id,
      followUpReminder: { status: "completed" },
    });
  };

  if (!followUp) return null;

  return (
    <CustomModal isOpen={isOpen} width="460px" className="p-6">
      <div className="flex w-full items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[0.65rem] font-medium uppercase tracking-wide text-secondary">
            Follow-up action
          </p>
          <h2 className="mt-1 truncate text-base font-semibold text-gray-800 dark:text-gray-100">
            {followUp.company_name || "Account follow-up"}
          </h2>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {followUp.reason || followUp.notes || "Choose the next action for this follow-up."}
          </p>
          {isAiSuggestion && followUp.notes && (
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
              {followUp.notes}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-200"
          aria-label="Close follow-up actions"
        >
          <X size={17} />
        </button>
      </div>

      <div className="mt-2 flex w-full flex-col gap-2">
        {canComplete && (
          <PrimaryButton
            onClick={handleComplete}
            disabled={isCompleting || isDismissing}
            isLoading={isCompleting || isDismissing}
            className="w-full"
            startContent={false}
          >
            Mark completed
          </PrimaryButton>
        )}

        {hasContact && (
          <>
            <SecondaryButton
              title={isScheduledFollowUp ? "Reschedule follow-up" : "Set a reminder"}
              onClick={onOpenReminder}
              className="w-full"
            />
            <SecondaryButton
              title="Send email"
              onClick={onOpenEmail}
              className="w-full"
            />
          </>
        )}

        {!hasContact && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
            Select a contact from the account to send an email or set a reminder.
          </p>
        )}

        <SecondaryButton
          title="Open account"
          onClick={onOpenAccount}
          className="w-full"
          disabled={!onOpenAccount}
        />
      </div>
    </CustomModal>
  );
};

export default FollowUpActionModal;
